import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAccessToken } from "../../use-access-token";
import { configuration } from "../../../configuration";
import { FetchError, GetCommentsDto } from "../../types";
import { queryKeys } from "../queryKeys";
import { CommentSubject } from "../../../constants";

type Variables = {
  subjectType: CommentSubject;
  subjectGuid: string;
  commentGuid: string;
};

const useDeleteCommentRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function deleteCommentRequest(variables: Variables) {
    const accessToken = await getAccessToken();

    const response = await fetch(`${configuration.apiRootUrl}/comments/${variables.commentGuid}`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      method: "DELETE",
    });

    if (!response.ok) {
      throw new FetchError(response);
    }
  }

  return { deleteCommentRequest };
};

const useDeleteComment = () => {
  const { deleteCommentRequest } = useDeleteCommentRequest();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteCommentRequest,
    onMutate: async (variables) => {
      await queryClient.cancelQueries(queryKeys.comments.getComments(variables.subjectGuid));
      const cachedData = queryClient.getQueryData<GetCommentsDto>(
        queryKeys.comments.getComments(variables.subjectGuid)
      );

      if (cachedData) {
        const optimisticallyUpdatedData: GetCommentsDto = {
          ...cachedData,
          comments: cachedData.comments.filter((comment) => comment.guid !== variables.commentGuid),
        };

        queryClient.setQueryData(queryKeys.comments.getComments(variables.subjectGuid), optimisticallyUpdatedData);
        return { cachedData };
      }
    },
    onError: (_error, variables, context) => {
      queryClient.setQueryData(queryKeys.comments.getComments(variables.subjectGuid), context?.cachedData);
    },
    onSuccess: (_data, variables) => {
      switch (variables.subjectType) {
        case CommentSubject.ActionItem:
          queryClient.refetchQueries(queryKeys.actionItems.getActionItemFeed(variables.subjectGuid));
          queryClient.refetchQueries(queryKeys.actionItems.getActionItemSummary(variables.subjectGuid));
          break;
        case CommentSubject.Issue:
          queryClient.refetchQueries(queryKeys.issues.getIssueFeed(variables.subjectGuid));
          break;
        case CommentSubject.TaskItem:
          queryClient.invalidateQueries(queryKeys.taskItems.getTaskItemFeed(variables.subjectGuid));
          break;
        case CommentSubject.Team:
          queryClient.invalidateQueries(queryKeys.teams.getTeamFeed(variables.subjectGuid));
          break;
      }
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries(queryKeys.comments.getComments(variables.subjectGuid));
    },
  });
};

export { useDeleteComment };
